import { FC, RefObject, useEffect, useImperativeHandle, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import * as vp from 'vopay';
import { useForm, withStyles, FieldData, Tooltip, TextArea } from 'vopay';
import * as Yup from 'yup';

import { Title } from 'UI';
import { useBillingFieldRenderer } from '../../utils/renderBillingField';
import agreementDetailsTooltipImg from './agreementDetailsTooltip.png';

import styles from './AgreementDetails.module.scss';

const cn = withStyles(styles);

const LEGAL_NOTES_KEY = 'LegalAgreementNotes';

// These agreement fields are inherited from the billing package and must not be edited here.
const normalizeKey = (key: string) => key.replace(/[^a-z0-9]/gi, '').toLowerCase();
const DISABLED_FIELD_KEYS = new Set(['plantype', 'currency', 'billingmethod'].map(normalizeKey));

export type AgreementDetailsHandle = {
  touchAllFields: () => void;
};

export type AgreementDetailsProps = {
  ref?: RefObject<AgreementDetailsHandle | null>;
  data: vp.AgreementDetails | undefined;
  currency: string;
  onValidChange: (isValid: boolean) => void;
  onValuesChange: (values: Record<string, string | boolean>) => void;
};

type FormValues = Record<string, string | boolean>;

const AgreementDetails: FC<AgreementDetailsProps> = ({ ref, data = {}, currency, onValidChange, onValuesChange }) => {
  const { t } = useTranslation('translations', {
    keyPrefix: 'R1AccountCreation.Billing.PackageDetails.AgreementDetails',
  });

  const renderBillingField = useBillingFieldRenderer(currency);

  const initialValues = useMemo<FormValues>(
    () =>
      Object.entries(data).reduce<FormValues>((acc, [, item]) => {
        acc[item.settings] = item.value ?? (item.type === 'boolean' ? false : '');
        return acc;
      }, {}),
    [],
  );

  const fields = useMemo<FieldData<FormValues>[]>(
    () =>
      Object.entries(data).map(([key, item]) => {
        const isLegalNotes = item.settings === LEGAL_NOTES_KEY;
        return {
          fieldName: item.settings,
          label: isLegalNotes
            ? () => (
                <span className={cn('labelWithTooltip')}>
                  {key}
                  <Tooltip
                    information={t('legalNotesTooltip', {
                      defaultValue: 'These notes will appear in the Notes section of the legal agreement.',
                    })}
                    type="hover"
                    variant="outlined"
                    icon="info"
                    position="rightTop"
                    buttonWidth="18px"
                    iconWidth="12"
                  />
                </span>
              )
            : () => key,
          visibility: () => item.visible,
          getInputState: () =>
            DISABLED_FIELD_KEYS.has(normalizeKey(key))
              ? 'disabled'
              : item.type === 'boolean' || isLegalNotes
                ? 'optional'
                : 'required',
          showField: () => true,
          validate: () =>
            item.type === 'boolean' ? Yup.boolean() : isLegalNotes ? Yup.string() : Yup.string().required(),
          renderField: isLegalNotes
            ? ({ getFormikData }) => {
                const fd = getFormikData<string>({});
                return (
                  <TextArea
                    name={fd.name}
                    value={String(fd.value ?? '')}
                    onChange={fd.onChange}
                    onBlur={fd.onBlur}
                    variant="outlineShadow"
                    placeholder={t('legalNotesPlaceholder', { defaultValue: 'Enter legal agreement notes...' })}
                    rows={4}
                  />
                );
              }
            : ({ getFormikData }) => renderBillingField(item.type, getFormikData),
          ...(['amount', 'percent', 'numeric'].includes(item.type) && {
            serializeField: (values: FormValues) => vp.sanitizeToNumeric(String(values[item.settings] ?? '')) ?? '',
          }),
        };
      }),
    [data],
  );

  const { ctx, renderFormElements } = useForm<FormValues>({
    config: fields,
    initialValues,
    onSubmit: () => {},
  });

  useEffect(() => {
    onValidChange(ctx.isValid);
  }, [ctx.values, ctx.isValid]);

  useEffect(() => {
    const serialized = Object.fromEntries(
      fields.map((f) => [
        f.fieldName,
        f.serializeField ? (f.serializeField(ctx.values) ?? '') : ctx.values[f.fieldName],
      ]),
    ) as FormValues;
    onValuesChange(serialized);
  }, [ctx.values]);

  useImperativeHandle(
    ref,
    () => ({
      touchAllFields: () => {
        const touched: Record<string, boolean> = {};
        for (const field of fields) touched[field.fieldName] = true;
        void ctx.setTouched(touched);
      },
    }),
    [fields, ctx],
  );

  const tooltipContent = (
    <div className={cn('tooltipContent')}>
      <Title level="5">{t('Tooltip.title', { defaultValue: 'Important!' })}</Title>
      <p className={cn('tooltipDescription')}>
        {t('Tooltip.description', {
          defaultValue:
            'These details will appear on the Legal Agreement provided to the company. Please ensure vigilance while configuring them manually.',
        })}
      </p>
      <img
        src={agreementDetailsTooltipImg}
        alt={t('Tooltip.imageAlt', { defaultValue: 'Agreement PDF preview' })}
        className={cn('tooltipImage')}
      />
    </div>
  );

  return (
    <div>
      <div className={cn('titleRow')}>
        <Title level="4">{t('title', { defaultValue: 'Agreement Details' })}</Title>
        <Tooltip
          information={tooltipContent}
          type="hover"
          variant="outlined"
          icon="info"
          position="rightTop"
          buttonWidth="18px"
          iconWidth="12"
          width="400px"
          informationClassName={cn('tooltipInformation')}
          hasArrow
        />
      </div>
      <div className={cn('grid')}>
        {renderFormElements({ config: fields.filter((f) => f.fieldName !== LEGAL_NOTES_KEY) })}
      </div>
      {fields.some((f) => f.fieldName === LEGAL_NOTES_KEY) && (
        <div className={cn('legalNotesField')}>
          {renderFormElements({ config: fields.filter((f) => f.fieldName === LEGAL_NOTES_KEY) })}
        </div>
      )}
    </div>
  );
};

export default AgreementDetails;
